import type React from "react";
import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import invariant from "ts-invariant";

import { useDeleteLesson, usePostFile, usePostLesson, usePutLesson } from "api/services/content/hooks";
import { type Lesson, type LessonFormData, LessonFormDataSchema } from "api/services/content/schemas/lessons";
import { useProfileContext } from "contexts/ProfileContext";
import { ColumnContainer, ConfirmTermsCheckbox, FormPageContainer, MediaField } from "routes/library/components";
import { Form, FormPageInnerContainer } from "routes/library/components/styles";
import { FormState } from "routes/patients/PatientProfile/components/PatientHeader/PatientHeaderInfo/PatientDataList/TreatmentSwitch/FormState";
import { ButtonContainer } from "routes/patients/PatientProfile/components/UserCourse/components/LibraryItem/LibraryItem";
import { BaseButton } from "shared/atoms/BaseButton";
import { ConfirmationPopup } from "shared/atoms/ConfirmationPopup";
import { DeleteIcon } from "shared/icons";
import { sortAlphabetically } from "utils/array/sort";
import { reportError } from "utils/errorReporting";
import { useTargetJointOptions } from "utils/form";
import { useNavigationWarning } from "utils/hooks";
import { translatePainLocation } from "utils/translations";
import { waitForFile } from "utils/url/waitForFile";

import type { SelectedScreen } from "./components";
import { ContentSection, PreviewColumn, QuestionsSection } from "./components";
import { getDefaultValues } from "./components/utils";

interface HandleUploadFileArgs {
  file: File;
  formKey: `parts.${number}.translation.image_url` | `parts.${number}.translation.content.${number}.content`;
}

interface Props {
  lesson?: Lesson;
}

export const LessonForm: React.FC<Props> = ({ lesson }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useProfileContext();
  const targetJointOptions = useTargetJointOptions();
  const isEditing = !!lesson;
  const [isLoading, setIsLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showEditWarning, setShowEditWarning] = useState(isEditing);
  const [confirmedTerms, setConfirmedTerms] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState<SelectedScreen>({ screen: "text_lesson", index: 0 });
  const postFile = usePostFile();
  const postLesson = usePostLesson();
  const putLesson = usePutLesson();
  const deleteLesson = useDeleteLesson();

  invariant(profile, "Profile is required");

  const form = useForm<LessonFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: getDefaultValues({ lesson, locale: profile?.locale }),
    resolver: zodResolver(LessonFormDataSchema),
  });
  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
  } = form;

  useNavigationWarning({ warn: isDirty });

  const handleUploadFile = async ({ file, formKey }: HandleUploadFileArgs) => {
    setIsLoading(true);

    try {
      const result = await postFile.mutateAsync({
        file,
      });
      const { objectUrl } = result;

      // waitForFile is to ensure that the file is fully processed and available
      // before we set the url in the form. Adjust the maxAttempts and delayMs
      // in waitForFile args as needed based on expected processing time.
      await waitForFile({ url: objectUrl, maxAttempts: 10, delayMs: 3000 });
      form.setValue(formKey, objectUrl, { shouldDirty: true });
    } catch (error) {
      reportError("File upload failed", error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = handleSubmit(formData => {
    if (isEditing) {
      putLesson.mutateAsync(formData).then(({ tempLesson }) => {
        navigate("/library/your-library/confirmation", {
          state: {
            action: "edited",
            type: "lessons",
            item: { ...tempLesson, new: true },
          },
        });
      });
    } else {
      postLesson.mutateAsync(formData).then(({ tempLesson }) => {
        navigate("/library/your-library/confirmation", {
          state: {
            action: "created",
            type: "lessons",
            item: { ...tempLesson, new: true },
          },
        });
      });
    }
  });

  const onDelete = () => {
    if (lesson) {
      deleteLesson.mutateAsync(
        { lessonID: lesson.id },
        {
          onSuccess: () => {
            navigate("/library/your-library/lessons");
          },
        }
      );
    }
  };

  const translations = {
    button: {
      cancel: t("buttons.cancel"),
      create_lesson: t("buttons.create_lesson"),
      edit_lesson: t("buttons.edit_lesson"),
      upload_cover_image: t("buttons.upload_cover_image"),
    },
    formState: {
      error: t("library.lessons.form.error"),
      loading: t("library.lessons.form.loading"),
      success: t("library.lessons.form.success"),
    },
    title: t("library.exercises.fields.title"),
    target_joints: t("library.exercises.fields.target_joints"),
  };

  const isDisabled = postLesson.isLoading || putLesson.isLoading || !isDirty;
  const isLoadingAPI = postLesson.isLoading || putLesson.isLoading;
  const watchImage = form.watch("parts.0.translation.image_url");

  // Only used for previewing the lesson. Converts the form data to the same structure as a lesson.
  const partIndex = 0;
  const selectedPart = form.watch("parts")?.[partIndex];
  const formValues = {
    parts: [
      {
        questions: selectedPart?.questions || [],
        translation: {
          content: selectedPart?.translation.content || [],
          image_url: selectedPart?.translation.image_url || null,
          questions: selectedPart?.translation.questions || [],
          title: selectedPart?.translation.title || t("library.exercises.fields.title"),
        },
      },
    ],
    target_joints: form.watch("target_joints"),
  };

  return (
    <FormPageContainer
      header={isEditing ? t("library.exercises.form.edit_header") : t("library.exercises.form.create_header")}
      headerContent={
        isEditing && (
          <ButtonContainer
            onClick={e => {
              e.stopPropagation();
              setShowDialog(true);
            }}
          >
            <DeleteIcon />
          </ButtonContainer>
        )
      }
    >
      <FormPageInnerContainer>
        <ColumnContainer heading={t("library.exercises.form.header")}>
          <FormProvider {...form}>
            <Form onSubmit={onSubmit} data-testid="lesson-form" noValidate>
              <Controller
                name="target_joints"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth margin="normal" error={!!errors.target_joints}>
                    <InputLabel>{translations.target_joints}</InputLabel>
                    <Select
                      {...field}
                      multiple
                      error={!!errors.target_joints}
                      input={<OutlinedInput label={translations.target_joints} />}
                      renderValue={selected => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map(value => (
                            <Chip key={value} label={translatePainLocation({ painLocation: value })} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {sortAlphabetically({ list: targetJointOptions }).map(joint => (
                        <MenuItem key={joint.value} value={joint.value}>
                          {joint.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.target_joints && <FormHelperText>{t("errors.field.required")}</FormHelperText>}
                  </FormControl>
                )}
              />

              <ConfirmTermsCheckbox checked={confirmedTerms} onChange={e => setConfirmedTerms(e.target.checked)} />

              <Controller
                name={`parts.${partIndex}.translation.title`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={translations.title}
                    fullWidth
                    margin="normal"
                    error={!!errors.parts?.[partIndex]?.translation?.title}
                    helperText={!!errors.parts?.[partIndex]?.translation?.title && t("errors.field.required")}
                  />
                )}
              />

              <MediaField
                accept="image/*"
                error={
                  errors.parts?.[partIndex]?.translation?.image_url
                    ? t("errors.field.image_or_video_required")
                    : undefined
                }
                text={translations.button.upload_cover_image}
                isDisabled={!confirmedTerms}
                isLoading={isLoading}
                handleUploadFile={event => {
                  const { files } = event.target;
                  if (files && files.length > 0) {
                    handleUploadFile({ file: files[0], formKey: `parts.${partIndex}.translation.image_url` });
                  }
                }}
              />
              <ContentSection
                control={control}
                isLoading={isLoading}
                partIndex={partIndex}
                handleUploadFile={(event, pIndex, contentIndex) => {
                  const { files } = event.target;
                  if (files && files.length > 0) {
                    handleUploadFile({
                      file: files[0],
                      formKey: `parts.${pIndex}.translation.content.${contentIndex}.content`,
                    });
                  }
                }}
              />
              <QuestionsSection control={control} partIndex={partIndex} setSelectedScreen={setSelectedScreen} />

              <Box display="flex" flexDirection="column" alignItems="center">
                <Box display="flex" justifyContent="center" gap={2} mt={2} mb={2}>
                  <BaseButton
                    type="submit"
                    text={isEditing ? translations.button.edit_lesson : translations.button.create_lesson}
                    disabled={isDisabled}
                    loading={isLoadingAPI}
                    style={{ flexGrow: 0 }}
                  />

                  {isEditing && (
                    <BaseButton
                      text={translations.button.cancel}
                      variant="tertiaryBlue"
                      onClick={() => navigate(-1)}
                      style={{ flexGrow: 0 }}
                    />
                  )}
                </Box>

                <FormState
                  formState={postLesson.status || putLesson.status}
                  messages={{
                    error: translations.formState.error,
                    loading: translations.formState.loading,
                    success: translations.formState.success,
                  }}
                />
              </Box>
            </Form>
          </FormProvider>
        </ColumnContainer>

        <PreviewColumn
          image_url={watchImage || null}
          isLoading={isLoading}
          lesson={formValues}
          selectedScreen={selectedScreen}
          setSelectedScreen={setSelectedScreen}
        />
      </FormPageInnerContainer>

      <ConfirmationPopup
        isOpen={showEditWarning}
        title={t("library.lessons.form.edit_lesson_warning_title")}
        body={t("library.lessons.form.edit_lesson_warning_body")}
        onClickOutside={() => setShowEditWarning(false)}
        onConfirm={() => setShowEditWarning(false)}
      />

      <ConfirmationPopup
        isOpen={showDialog}
        title={t("library.lessons.form.delete_lesson_title")}
        onClickOutside={() => setShowDialog(false)}
        onConfirm={onDelete}
      />
    </FormPageContainer>
  );
};
